import React, { useState, useEffect } from 'react';
import { BookOpen, Download, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// A single "Download the manual" trigger (email-gated, lead-captured). Finds the
// LyphLine User Manual document and lets the visitor download it.
export const ManualDownloadLink = ({
  match = 'user manual',
  className = 'inline-flex items-center gap-2 border border-white/25 text-white font-semibold px-7 py-4 rounded-md hover:bg-white/10 transition-colors',
  label = 'Download the manual',
  Icon = BookOpen,
  testid = 'manual-download',
}) => {
  const [doc, setDoc] = useState(null);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/documents?category=instruction-manual`)
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : [];
        const found = list.find((d) => d.title.toLowerCase().includes(match.toLowerCase()));
        if (found) setDoc(found);
      })
      .catch(() => {});
  }, [match]);

  const urlFor = (d) => (d.external_url ? d.external_url : `${API_URL}${d.file_url}`);

  const submitGate = async () => {
    if (!email || !email.includes('@')) { toast.error('Please enter a valid email to get your download.'); return; }
    const url = urlFor(doc);
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/leads/document`, {
        email,
        document_title: doc.title,
        download_url: doc.external_url ? url : `${window.location.origin.replace(/\/$/, '')}${doc.file_url}`,
        category: doc.category,
      });
    } catch (e) { /* non-blocking */ }
    setSubmitting(false);
    window.open(url, '_blank', 'noopener, noreferrer');
    toast.success('Sent to your inbox, opening now too');
    setOpen(false);
    setEmail('');
  };

  if (!doc) return null;

  return (
    <>
      <button type="button" onClick={() => { setOpen(true); setEmail(''); }} className={className} data-testid={`${testid}-btn`}>
        <Icon className="h-5 w-5 text-[#CE8946]" /> {label}
      </button>

      {open && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid={`${testid}-gate`}>
          <div className="bg-white rounded-2xl p-7 max-w-md w-full relative text-left">
            <button onClick={() => setOpen(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-700" aria-label="Close"><X className="h-5 w-5" /></button>
            <h3 className="text-xl font-bold text-[#003456]">Get "{doc.title}"</h3>
            <p className="text-slate-600 text-sm mt-2">Enter your email and we'll send you the download link and open it now.</p>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submitGate(); }}
              placeholder="you@example.com"
              className="w-full px-4 py-2.5 rounded-md border border-slate-300 text-[#003456] text-sm mt-5 focus:outline-none focus:ring-2 focus:ring-[#CE8946]"
              data-testid={`${testid}-gate-email`}
              autoFocus
            />
            <button onClick={submitGate} disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 bg-[#003456] hover:bg-[#023F63] text-white font-bold px-6 py-3 rounded-md mt-4 transition-colors disabled:opacity-60" data-testid={`${testid}-gate-submit`}>
              {submitting ? 'Sending…' : (<>Get my download <Download className="h-4 w-4" /></>)}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ManualDownloadLink;
